/**
 * The rollout days of the Plan tab (spec D13, design 1b). `release` counts from the release date;
 * `spike` and `evergreen` ("Push this song") count from today. `reels` is the AI reels plan: its
 * days are made from stored settings (`reelsPlan.ts`), so it has no table here. music24-api turns
 * the days into draft Promote posts and one manual mark (`buildRollout`).
 *
 * The file imports only types, so every project can use it.
 */
import type { StudioMoment, StudioThemeId } from './studioThemes';

export type RolloutKind = 'release' | 'spike' | 'evergreen' | 'reels';

/** A release up to this many days ago still shows the release rollout (its last day is +7). */
export const RELEASE_ROLLOUT_WINDOW_DAYS = 7;

export type RolloutDayKey =
  | 'announce'
  | 'teaser'
  | 'countdown7'
  | 'countdown3'
  | 'countdown1'
  | 'outNow'
  | 'canvasTask'
  | 'secondReel'
  | `push${number}`
  // A post of an AI reels plan: the start day and the offset (`20261014-d4`, `reelsPlanDayKey`). The
  // second and the third post of a day add `p2` and `p3` (`20261014-d4p2`).
  | `${number}-d${number}`
  | `${number}-d${number}p${number}`;

export interface RolloutDay {
  key: RolloutDayKey;
  /** Days from the release date (release), from today (spike, evergreen) or from the start day (reels). */
  offsetDays: number;
  /**
   * The kit theme and variant whose file the post uses; null for a text post. A story and a feed
   * post have one variant per moment (`STORY_MOMENT_VARIANTS`, `FEED_MOMENT_VARIANTS`); `moment`
   * is the moment to make it with.
   */
  asset: { themeId: StudioThemeId; variant: string; moment?: StudioMoment } | null;
  format: 'post' | 'story' | 'reel';
  /** A task is a manual mark (the Canvas on Spotify), not a post. Nothing reminds the artist (owner decision 2026-10-09). */
  isTask: boolean;
}

/** The AI reel keeps `ReelModel` (Q3); the rollout names its file with this variant. */
export const AI_REEL_ROLLOUT_VARIANT = 'reel';

export const RELEASE_ROLLOUT: ReadonlyArray<RolloutDay> = [
  {
    key: 'announce',
    offsetDays: -14,
    asset: { themeId: 'story', variant: 'preSave', moment: 'preSave' },
    format: 'story',
    isTask: false,
  },
  {
    key: 'teaser',
    offsetDays: -10,
    asset: { themeId: 'aiReel', variant: AI_REEL_ROLLOUT_VARIANT },
    format: 'reel',
    isTask: false,
  },
  {
    key: 'countdown7',
    offsetDays: -7,
    asset: { themeId: 'story', variant: 'd7', moment: 'announce' },
    format: 'story',
    isTask: false,
  },
  {
    key: 'countdown3',
    offsetDays: -3,
    asset: { themeId: 'story', variant: 'd3', moment: 'announce' },
    format: 'story',
    isTask: false,
  },
  {
    key: 'countdown1',
    offsetDays: -1,
    asset: { themeId: 'story', variant: 'd1', moment: 'announce' },
    format: 'story',
    isTask: false,
  },
  {
    key: 'outNow',
    offsetDays: 0,
    asset: { themeId: 'feedPost', variant: 'outNow', moment: 'outNow' },
    format: 'post',
    isTask: false,
  },
  {
    // The Canvas mark: the artist adds the loop in Spotify for Artists and marks it in the plan (no email).
    key: 'canvasTask',
    offsetDays: 1,
    asset: { themeId: 'canvasLoop', variant: 'canvas' },
    format: 'post',
    isTask: true,
  },
  {
    key: 'secondReel',
    offsetDays: 7,
    asset: { themeId: 'aiReel', variant: AI_REEL_ROLLOUT_VARIANT },
    format: 'reel',
    isTask: false,
  },
];

export const SPIKE_ROLLOUT: ReadonlyArray<RolloutDay> = [
  {
    key: 'push1',
    offsetDays: 0,
    asset: { themeId: 'feedPost', variant: 'outNow', moment: 'outNow' },
    format: 'post',
    isTask: false,
  },
  {
    key: 'push2',
    offsetDays: 2,
    asset: { themeId: 'aiReel', variant: AI_REEL_ROLLOUT_VARIANT },
    format: 'reel',
    isTask: false,
  },
  {
    key: 'push3',
    offsetDays: 4,
    asset: { themeId: 'story', variant: 'outNow', moment: 'outNow' },
    format: 'story',
    isTask: false,
  },
  {
    key: 'push4',
    offsetDays: 6,
    asset: { themeId: 'artworkInMotion', variant: 'video' },
    format: 'reel',
    isTask: false,
  },
];

/**
 * "Push this song" of a released song (owner answer 8, 2026-10-07): one post a week for 6 weeks.
 * The user can untick weeks before "Add drafts" (`dayKeys` of `POST .../rollout`).
 */
export const EVERGREEN_ROLLOUT: ReadonlyArray<RolloutDay> = [
  {
    key: 'push1',
    offsetDays: 0,
    asset: { themeId: 'feedPost', variant: 'outNow', moment: 'outNow' },
    format: 'post',
    isTask: false,
  },
  {
    key: 'push2',
    offsetDays: 7,
    asset: { themeId: 'aiReel', variant: AI_REEL_ROLLOUT_VARIANT },
    format: 'reel',
    isTask: false,
  },
  {
    key: 'push3',
    offsetDays: 14,
    asset: { themeId: 'artworkInMotion', variant: 'video' },
    format: 'reel',
    isTask: false,
  },
  {
    key: 'push4',
    offsetDays: 21,
    asset: { themeId: 'story', variant: 'outNow', moment: 'outNow' },
    format: 'story',
    isTask: false,
  },
  {
    key: 'push5',
    offsetDays: 28,
    asset: { themeId: 'lyricCard', variant: 'square' },
    format: 'post',
    isTask: false,
  },
  {
    key: 'push6',
    offsetDays: 35,
    asset: { themeId: 'aiReel', variant: AI_REEL_ROLLOUT_VARIANT },
    format: 'reel',
    isTask: false,
  },
];

export function rolloutDays(kind: RolloutKind): ReadonlyArray<RolloutDay> {
  switch (kind) {
    case 'release':
      return RELEASE_ROLLOUT;
    case 'spike':
      return SPIKE_ROLLOUT;
    case 'evergreen':
      return EVERGREEN_ROLLOUT;
    case 'reels':
      // The days of an AI reels plan come from its settings (`reelsPlanDays`).
      return [];
  }
}

/**
 * The day key of an AI reels plan: 8 digits of the start day, then `-d` and the offset, then `p2` or
 * `p3` for the second and the third post of the day.
 */
const REELS_DAY_KEY_PATTERN = /^\d{8}-d(\d{1,3})(?:p[23])?$/;

/**
 * The day offset of a rollout key (`release.countdown7` gives -7, `evergreen.push2` gives 7,
 * `reels.20261014-d4` gives 4: the key of an AI reels plan carries its offset), or null for no key
 * or an unknown one. The Posts page shows it as "Rollout day -7" (owner request 2026-10-09).
 */
export function rolloutOffsetOfKey(rolloutKey: string | null | undefined): number | null {
  if (rolloutKey === null || rolloutKey === undefined) {
    return null;
  }
  const dot = rolloutKey.indexOf('.');
  const kind = rolloutKey.slice(0, dot);
  const key = rolloutKey.slice(dot + 1);
  if (kind === 'reels') {
    const match = REELS_DAY_KEY_PATTERN.exec(key);
    return match === null ? null : Number(match[1]);
  }
  if (kind !== 'release' && kind !== 'spike' && kind !== 'evergreen') {
    return null;
  }
  return rolloutDays(kind).find((day) => day.key === key)?.offsetDays ?? null;
}

/**
 * The release rollout day that a kit file is made for (design assets-rethink c1: "Rollout day -7"
 * on a ready file): the post day whose asset has the same type and variant. Null for a file that no
 * post day uses, and for an AI reel (two days use one; its posts name the day).
 */
export function releaseRolloutOffsetOfAsset(asset: { themeId: string; variant: string }): number | null {
  if (asset.themeId === 'aiReel') {
    return null;
  }
  const day = RELEASE_ROLLOUT.find(
    (one) =>
      !one.isTask && one.asset !== null && one.asset.themeId === asset.themeId && one.asset.variant === asset.variant
  );
  return day?.offsetDays ?? null;
}
